import {ProductTechnicalPropertyTypeIds} from "../../../../../../../../core/constants/enums";
import Utils from "../../../../../../../../core/services/utils/utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer} from "./shared";

/**
 * @param {*} value
 * @param {boolean} required
 * @returns {boolean}
 */
function cellHasInvalidBoolean(value, required) {
    if (value === undefined || value === null) return required;
    const s = String(value).trim();
    if (!s) {
        return required;
    }
    return Utils.tryConvertStringToBoolean(s) === undefined;
}

/**
 * @param {*} value
 * @returns {boolean|undefined} undefined = cell absent or empty (no config change from this column)
 */
function parseOptionalBooleanFlag(value) {
    if (value === undefined || value === null) return undefined;
    const s = String(value).trim();
    if (!s) return undefined;
    const t = Utils.tryConvertStringToBoolean(s);
    if (t !== undefined) return t;
    return s.toLowerCase() === "true";
}

/**
 * @param {*} entry
 * @param {*} property
 * @returns {Object}
 */
function buildProductPropertyOptionsRequest(entry, property) {
    const req = {
        propertyId: property.id,
        title: property.title,
        updateConfig: false,
        hasPreDefinedValues: property.config?.hasPreDefinedValues,
        isMultiSelect: property.config?.isMultiSelect,
        newOptions: [],
    };

    if (entry.hasPreDefinedValues !== undefined) {
        const parsedHasPreDefinedValues =
            parseOptionalBooleanFlag(entry.hasPreDefinedValues) ?? false;
        if (parsedHasPreDefinedValues !== property.config?.hasPreDefinedValues) {
            req.updateConfig = true;
            req.hasPreDefinedValues = parsedHasPreDefinedValues;
        }
    }

    if (entry.isMultiSelect !== undefined) {
        const parsedIsMultiSelect = parseOptionalBooleanFlag(entry.isMultiSelect) ?? false;
        if (parsedIsMultiSelect !== property.config?.isMultiSelect) {
            req.updateConfig = true;
            req.isMultiSelect = parsedIsMultiSelect;
        }
    }

    if (req.hasPreDefinedValues && entry.predefinedValues) {
        req.newOptions = String(entry.predefinedValues ?? "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
            .map((rawValue) => {
                const parsedValue = Utils.parseTechnicalPropertyValue(property.industryPropertyTypeId, rawValue);
                if (parsedValue === null || parsedValue === undefined) {
                    return null;
                }
                return parsedValue;
            })
            .filter(Boolean);
    }

    if (property.industryPropertyTypeId === ProductTechnicalPropertyTypeIds.boolean) {
        req.newOptions = undefined;
    }

    return req;
}

/**
 * Product property options import (per industry) — phased validation (see {@link BaseImportTransformer} run contract).
 * Resolves each row’s `propertyTitle` against the passed `properties` list and builds the same API request
 * payload as the legacy transformer.
 *
 * @param {Array<Object|null|undefined>} mappedData
 * @param {boolean} isFinalRun
 * @param {string|number} _industryId Reserved for parity with the import view (properties are keyed by industry).
 * @param {Array<*>} [properties]
 */
export class ProductPropertyOptionsImportTransformer extends BaseImportTransformer {
    async run(mappedData, isFinalRun = false, _industryId, properties = null) {
        const rows = mappedData ?? [];
        const list = properties ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, titleTrim: string, property: *, codes: string[]}[]} */
        const rowState = rows.map((item, index) => {
            const titleTrim = item?.propertyTitle?.trim() ?? "";
            const property = titleTrim
                ? list.find((p) => p.title?.toLowerCase() === titleTrim.toLowerCase()) ?? null
                : null;
            return {
                index,
                item,
                titleTrim,
                property,
                codes: [],
            };
        });

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.property || !rs.titleTrim) {
                rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (cellHasInvalidBoolean(rs.item.hasPreDefinedValues, true)) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
            if (cellHasInvalidBoolean(rs.item.isMultiSelect, false)) {
                rs.codes.push(PreventionInvalidityCode.INVALID_BOOLEAN);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                invalidityCodes: [...new Set(rs.codes)],
                originalRow: rs.item,
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) return null;
            if (issueIndexSet.has(index)) {
                return isFinalRun ? null : {propertyId: undefined};
            }
            const rs = rowState[index];
            return buildProductPropertyOptionsRequest(item, rs.property);
        });

        return {apiRequests, preventativeIssues};
    }
}
